import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Carousel,
  CarouselItem,
  CarouselControl,
  CarouselIndicators,
} from "reactstrap";
import catalogData from "./catalog.json";
import "bootstrap/dist/css/bootstrap.min.css";
import designs from "./designs.json";
import logo from "../assets/img/boxs_logo_new.png";

// Print styles for price view
const printStyles = `
  @media print {
    .price-view {
      background: white !important;
      color: black !important;
    }

    .price-view .shadow-sm {
      box-shadow: none !important;
      border: 1px solid #ddd !important;
    }

    .price-view .bg-light {
      background-color: #f8f9fa !important;
    }

    .price-view .text-primary {
      color: #007bff !important;
    }

    .price-view .text-success {
      color: #28a745 !important;
    }

    .price-view .text-info {
      color: #17a2b8 !important;
    }

    .price-view .text-muted {
      color: #6c757d !important;
    }

    .price-view .border {
      border: 1px solid #dee2e6 !important;
    }

    .price-view .rounded {
      border-radius: 0.25rem !important;
    }

    .price-view .table {
      border-collapse: collapse !important;
    }

    .price-view .table th,
    .price-view .table td {
      border: 1px solid #dee2e6 !important;
      padding: 0.5rem !important;
    }

    .price-view .table thead th {
      background-color: #f8f9fa !important;
      font-weight: bold !important;
    }
  }
`;

// Inject print styles
if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = printStyles;
  document.head.appendChild(styleSheet);
}

// Feature icon map
const FEATURE_ICONS = {
  dimensions: "https://img.icons8.com/ios-filled/50/ruler.png",
  soft_close_runners: "https://img.icons8.com/ios-filled/50/puzzle.png",
  matte_finishes: "https://img.icons8.com/ios-filled/50/paint-palette.png",
  bwr_hdhmr: "https://img.icons8.com/ios-filled/50/wood.png",
  easy_to_install: "https://img.icons8.com/ios-filled/50/drill.png",
  five_year_warranty: "https://img.icons8.com/ios-filled/50/medal.png",
};

// Features for each sub_category (parameterized)
const PRODUCT_FEATURES = {
  external_drawer_half: [
    "dimensions",
    "soft_close_runners",
    "matte_finishes",
    "bwr_hdhmr",
    "easy_to_install",
    "five_year_warranty",
  ],
  // Add more sub_category keys as needed
};

// Helper to parameterize sub_category or feature label
function parameterize(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

// Helper to humanize feature label
function humanize(str) {
  return str.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

// --- NEW: Tag set groupings and group name logic ---
const TAG_GROUPS = [
  { tags: ["box", "1_door", "wardrobe"], name: "1 Door Wardrobe Box" },
  { tags: ["box", "2_door", "wardrobe"], name: "2 Door Wardrobe Box" },
  { tags: ["box", "1_door", "floor"], name: "1 Door Floor Unit Box" },
  { tags: ["box", "2_door", "floor"], name: "2 Door Floor Unit Box" },
  { tags: ["box", "1_door", "wall"], name: "1 Door Wall Unit Box" },
  { tags: ["box", "2_door", "wall"], name: "2 Door Wall Unit Box" },
  { tags: ["frame"], name: "Frame" },
  { tags: ["drawer", "tandem"], name: "Tandem Drawer" },
  {
    tags: ["drawer", "internal", "full_width", "telescopic"],
    name: "Internal Drawer - Full Width",
  },
  {
    tags: ["drawer", "internal", "half_width", "telescopic"],
    name: "Internal Drawer - Half Width",
  },
  {
    tags: ["drawer", "external", "full_width", "telescopic"],
    name: "External Drawer - Full Width",
  },
  {
    tags: ["drawer", "external", "half_width", "telescopic"],
    name: "External Drawer - Half Width",
  },
  {
    tags: ["partition", "fixed", "full_width"],
    name: "Fixed Shelf – Full Width",
  },
  {
    tags: ["partition", "fixed", "half_width"],
    name: "Fixed Shelf – Half Width",
  },
  {
    tags: ["partition", "removable", "full_width"],
    name: "Removable Shelf – Full Width",
  },
  {
    tags: ["partition", "removable", "half_width"],
    name: "Removable Shelf – Half Width",
  },
  { tags: ["partition", "vertical"], name: "Vertical Partition" },
];

// Helper to generate dynamic group name for fixed shelves with inset/full_depth
function getDynamicFixedShelfName(tags, width) {
  if (tags.includes("fixed_shelf")) {
    if (tags.includes("full_width") && tags.includes("inset")) {
      return `Fixed Shelf – Width Inset${width ? " " + width : ""}`;
    }
    if (tags.includes("half_width") && tags.includes("inset")) {
      return `Fixed Shelf – Half Width Inset${width ? " " + width : ""}`;
    }
    if (tags.includes("full_width") && tags.includes("full_depth")) {
      return `Fixed Shelf – Full Depth${width ? " " + width : ""}`;
    }
    if (tags.includes("half_width") && tags.includes("full_depth")) {
      return `Fixed Shelf – Half Width Full Depth${width ? " " + width : ""}`;
    }
  }
  return null;
}

// Helper to match a product's tags to a group
function getGroupNameForTags(tags) {
  for (const group of TAG_GROUPS) {
    if (
      group.tags.length === tags.length &&
      group.tags.every((t) => tags.includes(t))
    ) {
      return group.name;
    }
  }
  // Fallback: join tags and humanize
  return tags.map(humanize).join(" / ");
}

// --- REPLACE groupByPage with groupByPage ---
function groupByPage(data) {
  const grouped = {};

  // Group products by TAG_GROUPS only, ignore page field from JSON
  data.forEach((row) => {
    let pageName = "Uncategorized";

    // Try to find a matching tag group for this product
    if (row.tags) {
      // Convert tags string to array (handle both string and array formats)
      const productTags =
        typeof row.tags === "string"
          ? row.tags.split(",").map((tag) => tag.trim())
          : Array.isArray(row.tags)
          ? row.tags
          : [];

      for (const group of TAG_GROUPS) {
        // Check if all group tags are present in the product tags
        if (group.tags.every((tag) => productTags.includes(tag))) {
          pageName = group.name;
          break;
        }
      }
    }

    if (!grouped[pageName]) {
      grouped[pageName] = {
        page: pageName,
        products: [],
        name: pageName,
      };
    }
    grouped[pageName].products.push(row);
  });

  // Define the specific order for pages
  const pageOrder = [
    "1 Door Wardrobe Box",
    "2 Door Wardrobe Box",
    "1 Door Floor Unit Box",
    "2 Door Floor Unit Box",
    "1 Door Wall Unit Box",
    "2 Door Wall Unit Box",
    "Frame",
    "Tandem Drawer",
    "Internal Drawer - Full Width",
    "Internal Drawer - Half Width",
    "External Drawer - Full Width",
    "External Drawer - Half Width",
    "Fixed Shelf – Full Width",
    "Fixed Shelf – Half Width",
    "Removable Shelf – Full Width",
    "Removable Shelf – Half Width",
    "Vertical Partition",
  ];

  // Create a new ordered object
  const orderedGrouped = {};

  // First, add pages in the specified order
  pageOrder.forEach((pageName) => {
    if (grouped[pageName]) {
      orderedGrouped[pageName] = grouped[pageName];
    }
  });

  // Then, add any remaining pages that weren't in the order list
  Object.keys(grouped).forEach((pageName) => {
    if (!pageOrder.includes(pageName)) {
      orderedGrouped[pageName] = grouped[pageName];
    }
  });

  return orderedGrouped;
}

const catalog = groupByPage(catalogData);

// Create ordered array based on pageOrder from groupByPage function
const orderedCatalogArray = [];
const pageOrder = [
  "1 Door Wardrobe Box",
  "2 Door Wardrobe Box",
  "1 Door Floor Unit Box",
  "2 Door Floor Unit Box",
  "1 Door Wall Unit Box",
  "2 Door Wall Unit Box",
  "Frame",
  "Tandem Drawer",
  "Internal Drawer - Full Width",
  "Internal Drawer - Half Width",
  "External Drawer - Full Width",
  "External Drawer - Half Width",
  "Fixed Shelf – Full Width",
  "Fixed Shelf – Half Width",
  "Removable Shelf – Full Width",
  "Removable Shelf – Half Width",
  "Vertical Partition",
];

pageOrder.forEach((pageName) => {
  if (catalog[pageName]) {
    orderedCatalogArray.push([pageName, catalog[pageName]]);
  }
});
// Add any remaining pages not in pageOrder
Object.entries(catalog).forEach(([pageKey, pageData]) => {
  if (!pageOrder.includes(pageKey)) {
    orderedCatalogArray.push([pageKey, pageData]);
  }
});

// Debug function to analyze page name differences
function analyzePageNameDifferences() {
  const differences = [];
  const tagBasedPages = new Set();
  const originalPages = new Set();

  catalogData.forEach((row) => {
    let tagBasedPageName = "Uncategorized";

    if (row.tags) {
      const productTags =
        typeof row.tags === "string"
          ? row.tags.split(",").map((tag) => tag.trim())
          : Array.isArray(row.tags)
          ? row.tags
          : [];

      for (const group of TAG_GROUPS) {
        if (group.tags.every((tag) => productTags.includes(tag))) {
          tagBasedPageName = group.name;
          break;
        }
      }
    }

    const originalPageName = row.page || "No page field";

    tagBasedPages.add(tagBasedPageName);
    originalPages.add(originalPageName);

    if (tagBasedPageName !== originalPageName) {
      differences.push({
        sku: row.sku,
        name: row.name,
        tags: row.tags,
        originalPage: originalPageName,
        tagBasedPage: tagBasedPageName,
      });
    }
  });

  return {
    differences,
    tagBasedPages: Array.from(tagBasedPages),
    originalPages: Array.from(originalPages),
  };
}

// Run the analysis
const pageAnalysis = analyzePageNameDifferences();

const skuMap = {};
catalogData.forEach((item) => {
  if (item.sku) {
    skuMap[item.sku] = item;
  }
});

// Image mapping for different product types based on tags
const PRODUCT_IMAGES = {
  // Shells
  "1d_wardrobe,box": [
    "/catalog-images/shell_1d_wardrobe/image_1.jpg",
    "/catalog-images/shell_1d_wardrobe/image_2.jpg",
    "/catalog-images/shell_1d_wardrobe/image_3.jpg",
  ],
  "2d_wardrobe,box": [
    "/catalog-images/shell_2d_wardrobe/image_1.jpg",
    "/catalog-images/shell_1d_wardrobe/image_2.jpg",
    "/catalog-images/shell_2d_wardrobe/image_3.jpg",
  ],
  // Internal Drawers
  "full_width,internal_drawer": [
    "/catalog-images/internal_drawer_full_width/image_1.jpg",
    "/catalog-images/internal_drawer_full_width/image_2.jpg",
  ],
  "half_width,internal_drawer": [
    "/catalog-images/internal_drawer_half_width/image_1.jpg",
    "/catalog-images/internal_drawer_half_width/image_2.jpg",
  ],
  // External Drawers
  "external_drawer,full_width": [
    "/catalog-images/external_drawer_full_width/image_1.jpg",
  ],
  "external_drawer,half_width": [
    // No images available
  ],
  // Fixed Shelves
  "fixed_shelf,full_width": [
    "/catalog-images/fixed_shelf_full_width/image_1.jpg",
  ],
  "fixed_shelf,half_width": [
    "/catalog-images/fixed_shelf_half_width/image_1.jpg",
  ],
  // Removable Shelves
  "full_width,removable_shelf": [
    "/catalog-images/removable_shelf_full_width/image_1.jpg",
  ],
  "half_width,removable_shelf": [
    "/catalog-images/removable_shelf_half_width/image_1.jpg",
  ],
  // Vertical Partitions
  vertical_partition: ["/catalog-images/vertical_partition/image_1.jpg"],
  // Hinged Doors
  "hinged_door,range_1": [
    "/catalog-images/hinged_door_range_1/image_1.jpg",
    "/catalog-images/hinged_door_range_1/image_2.jpg",
  ],
  "hinged_door,range_2": [
    "/catalog-images/hinged_door_range_1/image_1.jpg",
    "/catalog-images/hinged_door_range_1/image_2.jpg",
  ],
  "hinged_door,range_3": [
    "/catalog-images/hinged_door_range_1/image_1.jpg",
    "/catalog-images/hinged_door_range_1/image_2.jpg",
  ],
  "hinged_door,range_4": [
    "/catalog-images/hinged_door_range_1/image_1.jpg",
    "/catalog-images/hinged_door_range_1/image_2.jpg",
  ],
  "hinged_door,range_5": [
    "/catalog-images/hinged_door_range_1/image_1.jpg",
    "/catalog-images/hinged_door_range_1/image_2.jpg",
  ],
  // Drawer Fascia
  "drawer,drawer_fascia,range_1": [
    "/catalog-images/drawer_fascia_range_1/image_1.jpg",
  ],
  "drawer,drawer_fascia,range_2": [
    "/catalog-images/drawer_fascia_range_1/image_1.jpg",
  ],
  "drawer,drawer_fascia,range_3": [
    "/catalog-images/drawer_fascia_range_1/image_1.jpg",
  ],
  "drawer,drawer_fascia,range_4": [
    "/catalog-images/drawer_fascia_range_1/image_1.jpg",
  ],
  "drawer,drawer_fascia,range_5": [
    "/catalog-images/drawer_fascia_range_1/image_1.jpg",
  ],
};

// Default images for fallback
const DEFAULT_IMAGES = [];

// Helper function to get images for a product group
function getImagesForTags(tags) {
  const tagKey = tags.slice().sort().join(",");
  return PRODUCT_IMAGES[tagKey] || DEFAULT_IMAGES;
}

// Helper function to get images for a page
function getImagesForPage(pageName) {
  // Map page names to image paths
  const PAGE_IMAGES = {
    "1 Door Wardrobe Box": [
      "/catalog-images/shell_1d_wardrobe/image_1.jpg",
      "/catalog-images/shell_1d_wardrobe/image_2.jpg",
      "/catalog-images/shell_1d_wardrobe/image_3.jpg",
    ],
    "2 Door Wardrobe Box": [
      "/catalog-images/shell_2d_wardrobe/image_1.jpg",
      "/catalog-images/shell_2d_wardrobe/image_2.jpg",
      "/catalog-images/shell_2d_wardrobe/image_3.jpg",
    ],
    "1 Door Floor Unit Box": DEFAULT_IMAGES,
    "2 Door Floor Unit Box": DEFAULT_IMAGES,
    "1 Door Wall Unit Box": DEFAULT_IMAGES,
    "2 Door Wall Unit Box": DEFAULT_IMAGES,
    Frame: DEFAULT_IMAGES,
    "Internal Drawer - Full Width": [
      "/catalog-images/internal_drawer_full_width/image_1.jpg",
      "/catalog-images/internal_drawer_full_width/image_2.jpg",
    ],
    "Internal Drawer - Half Width": [
      "/catalog-images/internal_drawer_half_width/image_1.jpg",
      "/catalog-images/internal_drawer_half_width/image_2.jpg",
    ],
    "External Drawer - Half Width": [
      "/catalog-images/external_drawer_full_width/image_1.jpg",
    ],
    "Tandem Drawer": ["/catalog-images/external_drawer_full_width/image_1.jpg"],
    "Fixed Shelf – Full Width": [
      "/catalog-images/fixed_shelf_full_width/image_1.jpg",
    ],
    "Fixed Shelf – Half Width": [
      "/catalog-images/fixed_shelf_half_width/image_1.jpg",
    ],
    "Removable Shelf – Full Width": [
      "/catalog-images/removable_shelf_full_width/image_1.jpg",
    ],
    "Removable Shelf – Half Width": [
      "/catalog-images/removable_shelf_half_width/image_1.jpg",
    ],
    "Vertical Partition": ["/catalog-images/vertical_partition/image_1.jpg"],
    "Hinged Door – Range 1": [
      "/catalog-images/hinged_door_range_1/image_1.jpg",
      "/catalog-images/hinged_door_range_1/image_2.jpg",
    ],
    "Hinged Door – Range 2": [
      "/catalog-images/hinged_door_range_1/image_1.jpg",
      "/catalog-images/hinged_door_range_1/image_2.jpg",
    ],
    "Hinged Door – Range 3": [
      "/catalog-images/hinged_door_range_1/image_1.jpg",
      "/catalog-images/hinged_door_range_1/image_2.jpg",
    ],
    "Hinged Door – Range 4": [
      "/catalog-images/hinged_door_range_1/image_1.jpg",
      "/catalog-images/hinged_door_range_1/image_2.jpg",
    ],
    "Hinged Door – Range 5": [
      "/catalog-images/hinged_door_range_1/image_1.jpg",
      "/catalog-images/hinged_door_range_1/image_2.jpg",
    ],
    "Drawer Fascia – Range 1": [
      "/catalog-images/drawer_fascia_range_1/image_1.jpg",
    ],
    "Drawer Fascia – Range 2": [
      "/catalog-images/drawer_fascia_range_1/image_1.jpg",
    ],
    "Drawer Fascia – Range 3": [
      "/catalog-images/drawer_fascia_range_1/image_1.jpg",
    ],
    "Drawer Fascia – Range 4": [
      "/catalog-images/drawer_fascia_range_1/image_1.jpg",
    ],
    "Drawer Fascia – Range 5": [
      "/catalog-images/drawer_fascia_range_1/image_1.jpg",
    ],
    "Liftup Shutters": DEFAULT_IMAGES,
  };

  return PAGE_IMAGES[pageName] || DEFAULT_IMAGES;
}

// Image Carousel Component
function ImageCarousel({ images, title }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);

  const next = () => {
    if (animating) return;
    const nextIndex = activeIndex === images.length - 1 ? 0 : activeIndex + 1;
    setActiveIndex(nextIndex);
  };

  const previous = () => {
    if (animating) return;
    const nextIndex = activeIndex === 0 ? images.length - 1 : activeIndex - 1;
    setActiveIndex(nextIndex);
  };

  const goToIndex = (newIndex) => {
    if (animating) return;
    setActiveIndex(newIndex);
  };

  const slides = [];

  // Create slides with 2 images side by side
  for (let i = 0; i < images.length; i += 2) {
    const slideImages = images.slice(i, i + 2);
    slides.push(
      <CarouselItem
        onExiting={() => setAnimating(true)}
        onExited={() => setAnimating(false)}
        key={i}
      >
        <div className="d-flex gap-2 gap-md-3 justify-content-center">
          {slideImages.map((image, index) => (
            <div
              key={index}
              style={{ width: "calc(50% - 0.25rem)", maxWidth: "240px" }}
            >
              <img
                src={image}
                alt={`${title} Image ${i + index + 1}`}
                className="img-fluid rounded shadow-sm"
              />
            </div>
          ))}
          {/* If odd number of images, add empty space to maintain layout on desktop */}
          {slideImages.length === 1 && (
            <div
              className="d-none d-lg-block"
              style={{ width: "calc(50% - 0.25rem)" }}
            ></div>
          )}
        </div>
      </CarouselItem>
    );
  }

  return (
    <div className="d-flex flex-column align-items-center">
      <Carousel
        activeIndex={activeIndex}
        next={next}
        previous={previous}
        interval={false}
        className="w-100"
        style={{ maxWidth: "520px" }}
      >
        {slides}
        {images.length > 2 && (
          <>
            <CarouselControl
              direction="prev"
              directionText="Previous"
              onClickHandler={previous}
            />
            <CarouselControl
              direction="next"
              directionText="Next"
              onClickHandler={next}
            />
          </>
        )}
      </Carousel>
      {images.length > 2 && (
        <CarouselIndicators
          items={slides}
          activeIndex={activeIndex}
          onClickHandler={goToIndex}
        />
      )}
    </div>
  );
}

export default function ProductCatalog() {
  // Get view mode from URL parameters or path
  const urlParams = new URLSearchParams(window.location.search);
  const isPricingRoute = window.location.pathname.includes("pricing/");
  const VIEW_MODE = isPricingRoute
    ? "price"
    : urlParams.get("view") || "catalog"; // 'catalog' or 'price'

  // State to track which product groups are expanded
  const [expandedGroups, setExpandedGroups] = useState({});
  const ITEMS_PER_PAGE = 5;

  // Function to switch view mode
  const switchViewMode = (mode) => {
    const newUrl = new URL(window.location);
    newUrl.searchParams.set("view", mode);
    window.history.pushState({}, "", newUrl);
    window.location.reload(); // Reload to apply the new view mode
  };

  function getDimensionKeys(products) {
    // Find which of height, width, depth are present in any product
    const keys = ["Height", "Width", "Depth"];
    return keys.filter((k) =>
      products.some(
        (row) => row[k] !== undefined && row[k] !== null && row[k] !== ""
      )
    );
  }

  function getDimensionLabel(key) {
    if (key === "Height") return "H";
    if (key === "Width") return "W";
    if (key === "Depth") return "D";
    return key;
  }

  const STUB_FEATURES = [
    { key: "dimensions", label: "Dimensions" },
    { key: "soft_close_runners", label: "Soft-Close Runners" },
    { key: "matte_finishes", label: "Matte Finishes" },
    { key: "bwr_hdhmr", label: "BWR / HDHMR" },
    { key: "easy_to_install", label: "Easy to Install" },
    { key: "five_year_warranty", label: "5 Year Warranty" },
  ];
  const STUB_SPECS = [
    ["Runner", "Hettich Tandem"],
    ["Mounting", "External"],
    ["Mechanism", "Soft Close"],
    ["Finish Options", "White Matte, Metallic Silver, Anthracite"],
    ["Material", "BWR Ply / HDHMR"],
    ["Available Widths", "400 – 575 mm"],
    ["Available Heights", "150, 200 mm"],
    ["Depth", "575 mm"],
  ];
  const STUB_FOOTER = (
    <div className="mt-3 p-2 bg-light rounded">
      <strong>Notes:</strong>
      <br />
      Sizes can be trimmed on request for an additional cost.
    </div>
  );

  const SPEC_LABELS = {
    material: "Material",
    liner: "Liner",
    edges: "Edges",
    shelf_support: "Shelf Support",
    hinges: "Hinges",
    runner_mechanism: "Runner Mechanism",
    available_heights: "Available Heights",
    available_widths: "Available Widths",
    available_depths: "Available Depths",
  };

  const toggleExpanded = (tagKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [tagKey]: !prev[tagKey],
    }));
  };

  return (
    <Container className={`my-4 ${VIEW_MODE === "price" ? "price-view" : ""}`}>
      {/* Header with Boxs Logo */}
      <div className="text-center mb-4">
        <img
          src={logo}
          alt="Boxs Logo"
          style={{ height: "60px", maxWidth: "200px" }}
          className="mb-3"
        />
        <h1 className="h3 mb-0">
          {VIEW_MODE === "catalog" ? "Product Catalog" : "Price List"}
        </h1>
      </div>

      {orderedCatalogArray.map(([pageKey, { page, products, name }]) => {
        // Use the page name directly
        const title = name;
        let safeProducts = [];
        if (Array.isArray(products)) {
          safeProducts = products;
        } else if (products && typeof products === "object") {
          safeProducts = [products];
        }
        if (!Array.isArray(products)) {
          console.warn("Non-array products for", pageKey, products);
        }
        const images = getImagesForPage(name); // Use page name for images
        const features = STUB_FEATURES;
        // --- NEW: Collect all unique spec values for each key across products ---
        const specValues = {};
        safeProducts.forEach((p) => {
          if (p.spec) {
            Object.entries(p.spec).forEach(([k, v]) => {
              if (v !== undefined && v !== null && v !== "") {
                if (!specValues[k]) specValues[k] = new Set();
                // If value is a string with comma-separated numbers, split and add each
                if (typeof v === "string" && /^(\d+,\s*)+\d+$/.test(v)) {
                  v.split(",")
                    .map((s) => s.trim())
                    .forEach((val) => specValues[k].add(val));
                } else {
                  specValues[k].add(v);
                }
              }
            });
          }
        });
        // Also handle available sizes
        const heights = Array.from(
          new Set(safeProducts.map((p) => p.Height).filter(Boolean))
        );
        const widths = Array.from(
          new Set(safeProducts.map((p) => p.Width).filter(Boolean))
        );
        const depths = Array.from(
          new Set(safeProducts.map((p) => p.Depth).filter(Boolean))
        );
        if (heights.length) specValues["available_heights"] = new Set(heights);
        if (widths.length) specValues["available_widths"] = new Set(widths);
        if (depths.length) specValues["available_depths"] = new Set(depths);
        // Helper to format spec values
        function formatSpecValue(key, valueSet) {
          const values = Array.from(valueSet);
          // If all values are numbers, join with comma
          if (values.length > 1 && values.every((v) => !isNaN(Number(v)))) {
            return values.sort((a, b) => Number(a) - Number(b)).join(", ");
          }
          // Otherwise, join with \n
          // Remove duplicates and sort for consistency
          const uniqueSorted = Array.from(new Set(values)).sort();
          return uniqueSorted.join("\n");
        }
        // Restore dimKeys definition
        const dimKeys = getDimensionKeys(safeProducts);

        // Determine which products to show based on expanded state
        const isExpanded = expandedGroups[pageKey];
        const productsToShow = isExpanded
          ? safeProducts
          : safeProducts.slice(0, ITEMS_PER_PAGE);
        const hasMoreProducts = safeProducts.length > ITEMS_PER_PAGE;

        return (
          <div
            key={pageKey}
            className={`mb-5 p-3 rounded ${
              VIEW_MODE === "catalog" ? "bg-light" : "bg-white border"
            }`}
          >
            {/* Header: title, image */}
            <div className="header d-flex flex-column flex-md-row align-items-start justify-content-between mb-3 gap-3">
              <div className="flex-grow-1">
                <h1 className="h2 mb-0">{title}</h1>
              </div>
              <div className="w-100" style={{ maxWidth: "520px" }}>
                <ImageCarousel images={images} title={title} />
              </div>
            </div>
            {/* Specifications - Only show in catalog view */}
            {VIEW_MODE === "catalog" && (
              <div className="specs rounded mb-4 shadow-sm">
                <h2 className="h5 pb-2 mb-1 fw-bold">Specifications</h2>
                <Table bordered responsive size="sm" className="mb-0">
                  <tbody>
                    {Object.entries(SPEC_LABELS).map(([key, label]) =>
                      specValues[key] && specValues[key].size > 0 ? (
                        <tr key={key}>
                          <th>{label}</th>
                          <td className="text-break">
                            {formatSpecValue(key, specValues[key])}
                          </td>
                        </tr>
                      ) : null
                    )}
                  </tbody>
                </Table>
              </div>
            )}

            {/* Product Table */}
            <div className="ordering-table ordering-table-wrapper rounded shadow-sm">
              <h2 className="h5 pb-2 mb-1 fw-bold">
                {VIEW_MODE === "catalog" ? "Variants" : ""}
              </h2>
              <div className="table-responsive">
                <Table bordered responsive size="sm" className="mb-0">
                  <thead>
                    <tr>
                      {VIEW_MODE === "catalog" && (
                        <th>{dimKeys.map(getDimensionLabel).join(" x ")}</th>
                      )}
                      <th>SKU</th>
                      <th>Product Name</th>
                      {VIEW_MODE === "price" && <th>Price (₹)</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {productsToShow.map((row, i) => (
                      <tr key={i}>
                        {VIEW_MODE === "catalog" && (
                          <td>
                            {dimKeys
                              .map((k) => {
                                const value = row[k];
                                if (
                                  value !== undefined &&
                                  value !== null &&
                                  value !== ""
                                ) {
                                  // Remove decimal points from dimension values
                                  return typeof value === "string"
                                    ? value.replace(/\.0+$/, "")
                                    : Math.floor(Number(value)).toString();
                                }
                                return value;
                              })
                              .filter(
                                (v) => v !== undefined && v !== null && v !== ""
                              )
                              .join(" x ")}
                          </td>
                        )}
                        <td>{row.sku}</td>
                        <td>{row.name}</td>
                        {VIEW_MODE === "price" && (
                          <td className="text-end fw-bold">
                            {row.price
                              ? `₹${parseInt(row.price).toLocaleString()}`
                              : "-"}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {hasMoreProducts && (
                <div className="text-center mt-3">
                  <Button
                    color="primary"
                    outline
                    onClick={() => toggleExpanded(pageKey)}
                    size="sm"
                  >
                    {isExpanded
                      ? "Show Less"
                      : `Show More (${
                          safeProducts.length - ITEMS_PER_PAGE
                        } more)`}
                  </Button>
                </div>
              )}
              {VIEW_MODE === "catalog" && STUB_FOOTER}
              {VIEW_MODE === "price" && (
                <div className="mt-3 p-2 bg-light rounded">
                  <div className="row">
                    <div className="col-md-6">
                      <strong>Notes:</strong>
                      <br />• Delivery charges extra
                    </div>
                    <div className="col-md-6 text-end">
                      <small className="text-muted">
                        Generated on:{" "}
                        {new Date().toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </small>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* --- Design Inspirations Section --- */}
      {VIEW_MODE === "price" && (
        <div className="my-5">
          <h2 className="mb-4">Design Inspirations</h2>
          {designs.map((design, idx) => (
            <div
              key={idx}
              className="d-flex flex-wrap mb-5 align-items-start position-relative"
              style={{ background: "#fafbfc", borderRadius: 8, padding: 24 }}
            >
              {(() => {
                const allProducts = (design.sections || []).flatMap(
                  (section) => section.products || []
                );
                const total = allProducts.reduce((sum, prod) => {
                  const catalogProduct = skuMap[prod.sku];
                  const price =
                    catalogProduct && catalogProduct.price
                      ? Number(catalogProduct.price)
                      : undefined;
                  const subTotal =
                    price !== undefined ? price * (prod.quantity || 1) : 0;
                  return sum + subTotal;
                }, 0);
              })()}
              {/* Title and dimensions at the top */}
              <div style={{ width: "100%" }}>
                <h4 className="mb-1">{design.title}</h4>
                <div className="mb-3 text-muted">
                  Size: {Math.round((design.height / 304.8) * 2) / 2} ft. x{" "}
                  {Math.round((design.width / 304.8) * 2) / 2} ft.
                </div>
              </div>
              {/* Design image and shopping list */}
              <div className="d-flex flex-wrap gap-4">
                {/* Image left */}
                <div
                  style={{
                    flex: "1 1 300px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={design.image}
                    alt={design.title}
                    className="img-fluid rounded shadow-sm"
                    style={{ width: "100%", objectFit: "cover" }}
                  />
                </div>
                {/* Shopping list table right */}
                <div style={{ flex: "1 1 350px", minWidth: 300 }}>
                  {(design.sections || []).map((section, sidx) => {
                    const allProducts = section.products || [];
                    const total = allProducts.reduce((sum, prod) => {
                      const catalogProduct = skuMap[prod.sku];
                      const price =
                        catalogProduct && catalogProduct.price
                          ? Number(catalogProduct.price)
                          : undefined;
                      const subTotal =
                        price !== undefined ? price * (prod.quantity || 1) : 0;
                      return sum + subTotal;
                    }, 0);

                    const heightInFt =
                      Math.round((design.height / 304.8) * 2) / 2;
                    const widthInFt =
                      Math.round((design.width / 304.8) * 2) / 2;
                    const sqFt = Math.round(heightInFt * widthInFt * 4) / 4;
                    const sqFtPrice = sqFt > 0 ? total / sqFt : 0;

                    return (
                      <div key={sidx} className="mb-4">
                        <table className="table table-bordered table-sm">
                          <thead>
                            <tr>
                              <th>SKU</th>
                              <th>Qty</th>
                              <th>Price</th>
                              <th>Sub-total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(section.products || []).map((prod, i) => {
                              const catalogProduct = skuMap[prod.sku];
                              const price =
                                catalogProduct && catalogProduct.price
                                  ? Number(catalogProduct.price)
                                  : undefined;
                              const subTotal =
                                price !== undefined
                                  ? price * (prod.quantity || 1)
                                  : undefined;
                              return (
                                <tr key={i}>
                                  <td>{prod.sku}</td>
                                  <td>{prod.quantity}</td>
                                  <td>
                                    {price !== undefined
                                      ? `₹${price.toLocaleString()}`
                                      : "-"}
                                  </td>
                                  <td>
                                    {subTotal !== undefined
                                      ? `₹${subTotal.toLocaleString()}`
                                      : "-"}
                                  </td>
                                </tr>
                              );
                            })}
                            <tr
                              style={{ fontWeight: "bold", fontSize: "1.1em" }}
                            >
                              <td colSpan="3" className="text-end">
                                Total:
                              </td>
                              <td>₹{total.toLocaleString()}</td>
                            </tr>
                            <tr>
                              <td colSpan="3"></td>
                              <td>
                                ₹
                                {sqFtPrice.toLocaleString(undefined, {
                                  maximumFractionDigits: 0,
                                })}{" "}
                                / sq.ft.
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Container>
  );
}
